// <sanctum-form> custom element.
//
// Lifecycle: connectedCallback fetches the schema, renders the form
// into an open shadow root, and wires the submit handler. Attribute
// changes to endpoint-id, base-url, no-attribution, or no-draft trigger a
// re-render. Shadow DOM is {mode: "open"} per the proposal.

import { loadTurnstile, renderTurnstile, type TurnstileApi, type TurnstileWidgetId } from "./challenge";
import type { ChallengeConfig, FormSchemaResponse } from "./schema";
import { initDraftPersistence, type DraftPersistence } from "./draft";
import {
  buildForm,
  buildFormError,
  buildPausedNotice,
  buildSuccessNotice,
  nextInstanceId,
  type RenderContext,
} from "./render";
import { STYLES } from "./styles";
import { buildPayload, firstMissingRequired, submitForm } from "./submit";

const DEFAULT_BASE_URL = "https://forms.digitalsanctum.com.au";

export class SanctumFormElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["endpoint-id", "base-url", "no-attribution", "no-draft"];
  }

  private shadow: ShadowRoot;
  private instanceId: string = nextInstanceId();
  private schema: FormSchemaResponse | null = null;
  private draft: DraftPersistence | null = null;
  private challengeApi: TurnstileApi | null = null;
  private challengeWidgetId: TurnstileWidgetId | null = null;
  // Guard against concurrent renders triggered by rapid attribute churn.
  private renderToken = 0;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    // isConnected guards a no-op when the browser sets initial attrs
    // before connectedCallback fires.
    if (!this.isConnected) return;
    this.render();
  }

  private get endpointId(): string | null {
    return this.getAttribute("endpoint-id");
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || DEFAULT_BASE_URL;
  }

  private get noAttribution(): boolean {
    return this.hasAttribute("no-attribution");
  }

  private get noDraft(): boolean {
    return this.hasAttribute("no-draft");
  }

  // Tests + advanced integrations may need direct schema access. Read-only.
  public getSchema(): FormSchemaResponse | null {
    return this.schema;
  }

  private clearShadow(): void {
    if (this.challengeApi && this.challengeWidgetId !== null && this.challengeApi.remove) {
      this.challengeApi.remove(this.challengeWidgetId);
    }
    this.challengeApi = null;
    this.challengeWidgetId = null;
    this.draft?.dispose();
    this.draft = null;
    while (this.shadow.firstChild) {
      this.shadow.removeChild(this.shadow.firstChild);
    }
    const style = document.createElement("style");
    style.textContent = STYLES;
    this.shadow.appendChild(style);
  }

  private async render(): Promise<void> {
    const token = ++this.renderToken;
    this.clearShadow();

    const endpointId = this.endpointId;
    if (!endpointId) {
      this.shadow.appendChild(
        buildFormError("Missing endpoint-id attribute on <sanctum-form>."),
      );
      return;
    }

    let schema: FormSchemaResponse;
    try {
      schema = await this.fetchSchema(endpointId);
    } catch (err) {
      if (token !== this.renderToken) return; // superseded
      const message = err instanceof Error ? err.message : "Unable to load form.";
      this.shadow.appendChild(buildFormError(message));
      return;
    }

    if (token !== this.renderToken) return; // superseded by a newer render
    this.schema = schema;

    if (schema.status === "paused") {
      this.shadow.appendChild(buildPausedNotice());
      return;
    }

    const ctx: RenderContext = {
      instanceId: this.instanceId,
      noAttribution: this.noAttribution,
    };
    const form = buildForm(schema, ctx);
    if (!this.noDraft) {
      this.draft = initDraftPersistence(form, schema, this.baseUrl, endpointId);
    }
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.handleSubmit(form);
    });
    this.shadow.appendChild(form);
  }

  private async fetchSchema(endpointId: string): Promise<FormSchemaResponse> {
    const url = `${this.baseUrl.replace(/\/$/, "")}/submit/${endpointId}`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
    } catch {
      throw new Error("Could not reach the form service. Please try again.");
    }
    if (response.status === 404) {
      throw new Error("This form is unavailable. Please contact the site owner.");
    }
    if (!response.ok) {
      throw new Error("Unable to load form.");
    }
    const body = (await response.json()) as FormSchemaResponse;
    // Minimal shape guard so a malformed response produces a clean
    // error instead of a thrown TypeError deep in the renderer.
    if (!body || !Array.isArray(body.field_schema) || !body.settings) {
      throw new Error("Form schema is malformed.");
    }
    return body;
  }

  private async handleSubmit(form: HTMLFormElement, challengeToken?: string): Promise<void> {
    if (!this.schema || !this.endpointId) return;

    // Clear any previous error state.
    this.clearErrors(form);

    const payload = buildPayload(form, this.schema);

    // Client-side required check (belt-and-braces; backend enforces too).
    const missing = firstMissingRequired(payload, this.schema);
    if (missing) {
      this.showFieldError(form, missing, "This field is required.");
      return;
    }

    const submitBtn = form.querySelector<HTMLButtonElement>(
      'button[part="submit"]',
    );
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending…";
    }

    const outcome = await submitForm({
      endpointId: this.endpointId,
      baseUrl: this.baseUrl,
      schema: this.schema,
      payload,
      formEl: form,
      challengeToken,
    });

    if (outcome.kind === "success") {
      this.draft?.clear();
      this.clearShadow();
      this.shadow.appendChild(buildSuccessNotice(outcome.message));
      return;
    }

    if (outcome.kind === "challenge") {
      await this.showChallenge(form, outcome.challenge, outcome.message);
      return;
    }

    // Re-enable submit on error. Restore the configured label
    // (defaulting to "Send Message" only when render.ts didn't cache
    // one — defensive against partial renders) so a custom #3105
    // submit_label survives an error round-trip.
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.defaultLabel || "Send Message";
    }

    const err = outcome.error;
    if ((err.kind === "required" || err.kind === "invalid") && "field" in err) {
      this.showFieldError(form, err.field, err.message);
      return;
    }
    this.showFormError(form, err.message);
  }

  private async showChallenge(
    form: HTMLFormElement,
    config: ChallengeConfig,
    message: string,
  ): Promise<void> {
    const region = form.querySelector<HTMLElement>('[data-role="challenge"]');
    const messageEl = form.querySelector<HTMLElement>('[data-role="challenge-message"]');
    const widget = form.querySelector<HTMLElement>('[data-role="challenge-widget"]');
    const submitBtn = form.querySelector<HTMLButtonElement>('button[part="submit"]');
    if (!region || !messageEl || !widget) {
      this.showFormError(form, "Security verification could not be displayed. Please reload and try again.");
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    region.hidden = false;
    messageEl.textContent = message;
    region.scrollIntoView?.({ behavior: "smooth", block: "nearest" });

    try {
      const api = await loadTurnstile();
      this.challengeApi = api;
      if (this.challengeWidgetId !== null) {
        api.reset(this.challengeWidgetId);
        return;
      }
      this.challengeWidgetId = renderTurnstile(api, widget, config, {
        success: (token) => {
          messageEl.textContent = "Security verification complete. Sending your message…";
          void this.handleSubmit(form, token);
        },
        error: () => {
          messageEl.textContent = "Security verification could not be completed. Please try again.";
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.defaultLabel || "Send Message";
          }
        },
        expired: () => {
          messageEl.textContent = "Security verification expired. Please complete it again.";
          if (this.challengeWidgetId !== null) api.reset(this.challengeWidgetId);
        },
      });
    } catch (error) {
      messageEl.textContent = error instanceof Error
        ? error.message
        : "Security verification could not load. Please try again.";
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.defaultLabel || "Send Message";
      }
    }
  }

  private clearErrors(form: HTMLFormElement): void {
    const banner = form.querySelector<HTMLElement>(
      '[data-role="form-error-banner"]',
    );
    if (banner) banner.textContent = "";
    form
      .querySelectorAll<HTMLElement>('[data-error-for]')
      .forEach((el) => {
        el.textContent = "";
      });
  }

  private showFieldError(
    form: HTMLFormElement,
    fieldName: string,
    message: string,
  ): void {
    const slot = form.querySelector<HTMLElement>(
      `[data-error-for="${cssEscape(fieldName)}"]`,
    );
    if (slot) {
      slot.textContent = message;
      const input = form.querySelector<HTMLElement>(
        `[name="${cssEscape(fieldName)}"]`,
      );
      input?.focus();
    } else {
      // Fallback: if we can't find the slot, surface at form level.
      this.showFormError(form, message);
    }
  }

  private showFormError(form: HTMLFormElement, message: string): void {
    const banner = form.querySelector<HTMLElement>(
      '[data-role="form-error-banner"]',
    );
    if (banner) {
      banner.textContent = message;
    }
  }
}

// CSS.escape polyfill for ancient happy-dom builds that lack it.
function cssEscape(value: string): string {
  if (typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS?.escape === "function") {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(value);
  }
  return value.replace(/"/g, '\\"');
}

// Register once. Guards against double-registration when the embed
// script is accidentally loaded twice (caching edge cases).
if (typeof customElements !== "undefined" && !customElements.get("sanctum-form")) {
  customElements.define("sanctum-form", SanctumFormElement);
}
